import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TopoBackground } from './TopoBackground';
import { HeroMock } from './HeroMock';
import { fadeInUp, staggerContainer, revealProps, EASE } from './animations';

/**
 * The PatronEZ hero — instrument-editorial, calm-first (TOPPAGE_REDESIGN.md).
 * No above-the-fold auto-motion; the product mock is user-driven (HeroMock).
 * LCP element is the headline text, not the mock. Drops the old unprovable
 * "9 seconds before your competitor" claim in favour of a mechanism promise.
 */
function jstClock(): string {
  // Asia/Tokyo HH:MM, computed live so the "LIVE" pill is never frozen.
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Tokyo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date());
  } catch {
    return '--:--';
  }
}

export function HeroSection() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [clock, setClock] = useState(jstClock);

  useEffect(() => {
    const id = window.setInterval(() => setClock(jstClock()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const stats = [
    { v: t('hero.stat_agents_v'), l: t('hero.stat_agents_l') },
    { v: t('hero.stat_registers_v'), l: t('hero.stat_registers_l') },
    { v: t('hero.stat_cost_v'), l: t('hero.stat_cost_l') },
  ];

  return (
    <section className="relative isolate overflow-hidden">
      <TopoBackground />

      <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left — copy */}
          <motion.div
            className="lg:col-span-7"
            variants={staggerContainer}
            {...revealProps(!!reduced)}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2.5 rounded-full border border-ink-200 bg-white/80 backdrop-blur px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-ink-700 shadow-soft mb-7"
            >
              <span className="relative inline-flex h-1.5 w-1.5">
                {!reduced && (
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                )}
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              <span className="num tracking-[0.18em] text-ink-900">{t('hero.eyebrow_label')}</span>
              <span className="h-3 w-px bg-ink-200" />
              <span className="num tracking-[0.14em] text-ink-500">
                {t('hero.eyebrow_city')} · <span className="tabular-nums">{clock}</span> JST
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="display serif-jp font-medium tracking-tight leading-[1.02] text-balance text-ink-900 text-[2.5rem] sm:text-5xl md:text-[3.4rem] lg:text-[4rem]"
            >
              {t('hero.h_a')}{' '}
              <span className="relative inline-block">
                <span className="text-ink-900">{t('hero.h_accent')}</span>
                <span
                  aria-hidden
                  className="absolute -bottom-1 left-0 right-0 h-[7px] bg-accent-500/25 -z-10 rounded-sm"
                />
              </span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="mt-7 max-w-[34rem] text-[15.5px] md:text-base text-ink-600 leading-relaxed text-pretty"
            >
              {t('hero.sub')}
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3"
            >
              <Link
                to="/signup"
                className="inline-flex h-11 items-center gap-2 px-5 rounded-md bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800 transition-colors"
              >
                {t('hero.primary_cta')}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/demo"
                className="inline-flex h-11 items-center gap-2 px-5 rounded-md border border-ink-300 text-ink-900 text-sm font-semibold hover:bg-ink-100 transition-colors"
              >
                {t('hero.demo_cta')}
              </Link>
              <span className="text-[12px] text-ink-500 sm:ml-1">{t('hero.no_card')}</span>
            </motion.div>

            {/* Honest, buyer-legible stat trio (no fabricated 導入社数; human-gating in the sub) */}
            <motion.dl
              variants={fadeInUp}
              className="mt-12 grid grid-cols-3 gap-6 max-w-md border-t border-ink-200 pt-6"
            >
              {stats.map((s) => (
                <div key={s.l}>
                  <dt className="num text-[2.05rem] md:text-[2.3rem] font-medium text-ink-900 tracking-tight leading-none tabular-nums">
                    {s.v}
                  </dt>
                  <dd className="mt-2 text-[10.5px] uppercase tracking-[0.16em] text-ink-500 leading-tight">
                    {s.l}
                  </dd>
                </div>
              ))}
            </motion.dl>
          </motion.div>

          {/* Right — user-driven product mock */}
          <motion.div
            className="lg:col-span-5"
            initial={reduced ? false : { opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
          >
            <HeroMock />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
